import { useEffect } from "react";

const SITE_NAME = "QaTestology";
const DEFAULT_IMAGE = "/og/og-default.svg";

/**
 * Lightweight SEO head manager for an SPA. Sets:
 *  - document.title
 *  - meta[name="description"]
 *  - meta[name="keywords"]
 *  - canonical link
 *  - Open Graph (og:title/description/image/url/type)
 *  - Twitter Card
 *
 * Direct DOM writes are sufficient because Googlebot renders JS during
 * indexing and social crawlers (LinkedIn, X) fetch during share previews.
 */
function upsertMeta(attr, name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoHead({ title, description, keywords, image, path, type = "website" }) {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — QA & Automation Testing Practice Site`;
    document.title = fullTitle;

    const url = path
      ? `${window.location.origin}${path}`
      : window.location.origin + window.location.pathname;
    const img = image
      ? (image.startsWith("http") ? image : `${window.location.origin}${image}`)
      : `${window.location.origin}${DEFAULT_IMAGE}`;

    upsertMeta("name", "description", description || "Free demo website to practise UI automation, browser testing and API testing. Selenium, Playwright, Cypress-friendly with stable data-testids on every element.");
    upsertMeta("name", "keywords", keywords || "software testing practice, automation practice website, selenium practice site, playwright practice site, cypress practice, api testing website, ui automation practice, qa demo website");
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || "Free demo website to practise UI automation, browser testing and API testing.");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", img);
  }, [title, description, keywords, image, path, type]);
  return null;
}
